import { getCookie } from "./cookies";

/**
 * Maps the sessionStorage cart into the place/unified swagpacks body.
 *
 * @param {Array<object>} cart - Checkout cart lines.
 * @returns {Array<object>} Swagpacks for the checkout APIs.
 */
export function buildCheckoutSwagpacks(cart) {
  return (cart || []).map((item) => ({
    id: item.pack_id ? parseInt(item.pack_id, 10) : null,
    inventory_id: item.inventory_id ? parseInt(item.inventory_id, 10) : null,
    quantity: item.selected_quantity,
    products: [
      {
        product_id: parseInt(item.product_id, 10),
        variants: [
          {
            id: parseInt(item.variant_id, 10),
            sku: item.variant_sku || item.sku || "",
            variant_sku: item.variant_sku || item.sku || "",
            size: item.selected_size,
            quantity: item.selected_quantity,
            color: item.selected_color || null,
            purchase_price: item.selling_price,
            gst_percentage: item.gst_percentage,
          },
        ],
      },
    ],
  }));
}

/**
 * New place idempotency key for this Pay click. Next Pay must use a new key.
 *
 * @returns {string} UUID, 36 chars.
 */
export function newPlaceIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `place_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Extra Razorpay Checkout options for local/dev only.
 * Hides dashboard offers so a stale CRED/UPI offer cannot fail test payments.
 * Production builds on a real host get `{}` and keep offers.
 *
 * @returns {object} Razorpay options to merge, or `{}` in production.
 */
export function razorpayLocalCheckoutOptions() {
  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  const isLocalHost = host === "localhost" || host === "127.0.0.1";
  if (process.env.NODE_ENV === "production" && !isLocalHost) {
    return {};
  }
  return {
    offers: [],
    config: {
      display: {
        hide: [{ method: "offer" }],
      },
    },
  };
}

/**
 * Polls until the webhook activates the place, or until timeout / FAILED.
 *
 * @param {string} razorpayOrderId - Razorpay order id from createOrder.
 * @param {{ intervalMs?: number, timeoutMs?: number }} [options]
 * @returns {Promise<{ checkout_state: string, shipment_ids: number[], last_error?: string|null }>}
 */
export async function pollCheckoutActivated(
  razorpayOrderId,
  options = {},
) {
  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 90000;
  const started = Date.now();
  let lastState = null;
  let lastError = null;

  while (Date.now() - started < timeoutMs) {
    const result = await fetchCheckoutStatus(razorpayOrderId);
    lastState = result?.checkout_state ?? null;
    lastError = result?.last_error ?? null;

    if (lastState === "ACTIVATED") {
      return result;
    }
    if (lastState === "FAILED") {
      const error = new Error(
        lastError || "Payment succeeded but the order could not be confirmed. Please contact support.",
      );
      error.code = "CHECKOUT_FAILED";
      throw error;
    }

    await sleep(intervalMs);
  }

  const timeout = new Error(
    lastState === "CAPTURED" || lastState === "HELD"
      ? "Payment received. Your order is still confirming — check My Orders in a moment, or contact support."
      : "Order confirmation timed out. If you were charged, contact support. Do not pay again until you check My Orders.",
  );
  timeout.code = "CHECKOUT_TIMEOUT";
  timeout.checkout_state = lastState;
  throw timeout;
}

/**
 * Loads checkout_state for a Razorpay order.
 *
 * @param {string} razorpayOrderId - Razorpay order id.
 * @returns {Promise<object|null>} Status payload, or null when not found.
 */
async function fetchCheckoutStatus(razorpayOrderId) {
  const headers = { "x-platform": "web" };
  const token = getCookie("auth_token");
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }
  const response = await fetch(
    `${process.env.REACT_APP_URL}/payment/checkout-status/${encodeURIComponent(razorpayOrderId)}`,
    { headers },
  );
  const data = await response.json();
  if (!response.ok || data?.success === false) {
    return null;
  }
  return data.result ?? null;
}

/**
 * @param {number} ms - Milliseconds to wait.
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
